/**
 * Path-finding utilities for word ladder puzzles.
 * These functions find shortest paths between words by changing one letter at a time.
 */

/**
 * Creates path-finding functions bound to specific dictionaries.
 * @param {Set} primaryDictionary - Set of primary valid words
 * @param {Set} secondaryDictionary - Set of secondary valid words (optional, defaults to empty)
 * @returns {Object} Object containing all path-finding functions
 */
export function createPathFinder(primaryDictionary, secondaryDictionary = new Set()) {
  // Check if a word is valid (in either dictionary)
  const isValidWord = (word) => primaryDictionary.has(word) || secondaryDictionary.has(word)

  // Find shortest path length between two words using only primary dictionary (BFS)
  const findShortestPathLength = (startWord, endWord) => {
    if (startWord === endWord) return 0

    const queue = [[startWord, 0]]
    const visited = new Set([startWord])

    while (queue.length > 0) {
      const [currentWord, dist] = queue.shift()

      for (let i = 0; i < currentWord.length; i++) {
        for (let c = 97; c <= 122; c++) {
          const char = String.fromCharCode(c)
          if (char === currentWord[i]) continue

          const newWord = currentWord.slice(0, i) + char + currentWord.slice(i + 1)

          if (newWord === endWord) {
            return dist + 1
          }

          if (primaryDictionary.has(newWord) && !visited.has(newWord)) {
            visited.add(newWord)
            queue.push([newWord, dist + 1])
          }
        }
      }
    }

    return Infinity // No path found
  }

  // Find shortest path length using BOTH dictionaries
  const findShortestPathLengthAllWords = (startWord, endWord) => {
    if (startWord === endWord) return 0

    const queue = [[startWord, 0]]
    const visited = new Set([startWord])

    while (queue.length > 0) {
      const [currentWord, dist] = queue.shift()

      for (let i = 0; i < currentWord.length; i++) {
        for (let c = 97; c <= 122; c++) {
          const char = String.fromCharCode(c)
          if (char === currentWord[i]) continue

          const newWord = currentWord.slice(0, i) + char + currentWord.slice(i + 1)

          if (newWord === endWord) {
            return dist + 1
          }

          if (isValidWord(newWord) && !visited.has(newWord)) {
            visited.add(newWord)
            queue.push([newWord, dist + 1])
          }
        }
      }
    }

    return Infinity // No path found
  }

  // Find shortest path between two words using only primary dictionary (BFS)
  const findShortestPath = (startWord, endWord) => {
    if (startWord === endWord) return [startWord]

    const queue = [[startWord, [startWord]]]
    const visited = new Set([startWord])

    while (queue.length > 0) {
      const [currentWord, path] = queue.shift()

      for (let i = 0; i < currentWord.length; i++) {
        for (let c = 97; c <= 122; c++) {
          const char = String.fromCharCode(c)
          if (char === currentWord[i]) continue

          const newWord = currentWord.slice(0, i) + char + currentWord.slice(i + 1)

          if (newWord === endWord) {
            return [...path, newWord]
          }

          if (primaryDictionary.has(newWord) && !visited.has(newWord)) {
            visited.add(newWord)
            queue.push([newWord, [...path, newWord]])
          }
        }
      }
    }

    return null // No path found
  }

  // Find shortest path between two words using BOTH dictionaries (BFS)
  const findShortestPathAllWords = (startWord, endWord) => {
    if (startWord === endWord) return [startWord]

    const queue = [[startWord, [startWord]]]
    const visited = new Set([startWord])

    while (queue.length > 0) {
      const [currentWord, path] = queue.shift()

      for (let i = 0; i < currentWord.length; i++) {
        for (let c = 97; c <= 122; c++) {
          const char = String.fromCharCode(c)
          if (char === currentWord[i]) continue

          const newWord = currentWord.slice(0, i) + char + currentWord.slice(i + 1)

          if (newWord === endWord) {
            return [...path, newWord]
          }

          if (isValidWord(newWord) && !visited.has(newWord)) {
            visited.add(newWord)
            queue.push([newWord, [...path, newWord]])
          }
        }
      }
    }

    return null // No path found
  }

  // Find shortest path, excluding a specific position for the first move
  // This prevents suggesting the same letter position that was just changed
  const findShortestPathExcluding = (startWord, endWord, excludePosition) => {
    if (startWord === endWord) return [startWord]
    if (excludePosition < 0) return findShortestPath(startWord, endWord)

    const queue = [[startWord, [startWord], true]] // [word, path, isFirstMove]
    const visited = new Set([startWord])

    while (queue.length > 0) {
      const [currentWord, path, isFirstMove] = queue.shift()

      for (let i = 0; i < currentWord.length; i++) {
        // Skip the excluded position on the first move only
        if (isFirstMove && i === excludePosition) continue

        for (let c = 97; c <= 122; c++) {
          const char = String.fromCharCode(c)
          if (char === currentWord[i]) continue

          const newWord = currentWord.slice(0, i) + char + currentWord.slice(i + 1)

          if (newWord === endWord) {
            return [...path, newWord]
          }

          if (primaryDictionary.has(newWord) && !visited.has(newWord)) {
            visited.add(newWord)
            queue.push([newWord, [...path, newWord], false])
          }
        }
      }
    }

    return null // No path found
  }

  // Find shortest path using both dictionaries, excluding a specific position for the first move
  const findShortestPathAllWordsExcluding = (startWord, endWord, excludePosition) => {
    if (startWord === endWord) return [startWord]
    if (excludePosition < 0) return findShortestPathAllWords(startWord, endWord)

    const queue = [[startWord, [startWord], true]] // [word, path, isFirstMove]
    const visited = new Set([startWord])

    while (queue.length > 0) {
      const [currentWord, path, isFirstMove] = queue.shift()

      for (let i = 0; i < currentWord.length; i++) {
        // Skip the excluded position on the first move only
        if (isFirstMove && i === excludePosition) continue

        for (let c = 97; c <= 122; c++) {
          const char = String.fromCharCode(c)
          if (char === currentWord[i]) continue

          const newWord = currentWord.slice(0, i) + char + currentWord.slice(i + 1)

          if (newWord === endWord) {
            return [...path, newWord]
          }

          if (isValidWord(newWord) && !visited.has(newWord)) {
            visited.add(newWord)
            queue.push([newWord, [...path, newWord], false])
          }
        }
      }
    }

    return null // No path found
  }

  // Find the last index in player's path that's still on AN optimal path
  // Returns { divergeIndex, optimalPathFromThere }
  // useSecondary: if true, use both dictionaries; if false, use primary only
  const findDivergencePoint = (startWord, endWord, playerPath, useSecondary = false) => {
    const getPathLength = useSecondary ? findShortestPathLengthAllWords : findShortestPathLength
    const getPath = useSecondary ? findShortestPathAllWords : findShortestPath

    const optimalLength = getPathLength(startWord, endWord)
    if (optimalLength === Infinity) return { divergeIndex: 0, optimalPathFromThere: null }

    const fullPath = [startWord, ...playerPath]

    for (let i = 0; i < fullPath.length; i++) {
      const word = fullPath[i]
      const distToEnd = getPathLength(word, endWord)
      const expectedDist = optimalLength - i

      if (distToEnd > expectedDist) {
        const lastGoodIndex = Math.max(0, i - 1)
        const lastGoodWord = fullPath[lastGoodIndex]
        const pathFromThere = getPath(lastGoodWord, endWord)
        return {
          divergeIndex: lastGoodIndex,
          lastGoodWord,
          optimalPathFromThere: pathFromThere
        }
      }
    }

    // No divergence - player is still on an optimal path
    const currentWord = fullPath[fullPath.length - 1]
    return {
      divergeIndex: -1,
      lastGoodWord: currentWord,
      optimalPathFromThere: getPath(currentWord, endWord)
    }
  }

  return {
    isValidWord,
    findShortestPathLength,
    findShortestPathLengthAllWords,
    findShortestPath,
    findShortestPathAllWords,
    findShortestPathExcluding,
    findShortestPathAllWordsExcluding,
    findDivergencePoint
  }
}

/**
 * Compute which letter index differs between two words (for hint display)
 * @param {string} fromWord - The starting word
 * @param {string} toWord - The target word
 * @returns {number} The index of the first differing letter, or -1 if none
 */
export const getLetterDiffIndex = (fromWord, toWord) => {
  if (!fromWord || !toWord) return -1
  for (let j = 0; j < fromWord.length && j < toWord.length; j++) {
    if (fromWord[j] !== toWord[j]) return j
  }
  return -1
}

/**
 * Simulates the LETTER hint feature behavior.
 * Given a current position and path history, returns what letter position should be hinted.
 *
 * @param {Object} pathFinder - Path finder created with createPathFinder
 * @param {string} startWord - The puzzle's start word
 * @param {string} endWord - The puzzle's end word
 * @param {string} currentWord - The player's current word
 * @param {string[]} currentPath - The player's path (not including start word)
 * @returns {Object} { hintLetterIdx, nextOptimalWord, wasReverted, revertedTo }
 */
export function simulateLetterHint(pathFinder, startWord, endWord, currentWord, currentPath) {
  // Check for divergence
  const divergence = pathFinder.findDivergencePoint(startWord, endWord, currentPath)

  let workingWord = currentWord
  let workingPath = currentPath
  let wasReverted = false
  let revertedTo = null

  if (divergence.divergeIndex >= 0) {
    // Player has diverged - would revert
    wasReverted = true
    if (divergence.divergeIndex === 0) {
      workingPath = []
      workingWord = startWord
      revertedTo = startWord
    } else {
      workingPath = currentPath.slice(0, divergence.divergeIndex)
      workingWord = workingPath[workingPath.length - 1] || startWord
      revertedTo = workingWord
    }
  }

  // Calculate which position was changed in the previous move
  let prevChangedPosition = -1
  if (workingPath.length > 0) {
    const previousWord = workingPath.length === 1 ? startWord : workingPath[workingPath.length - 2]
    prevChangedPosition = getLetterDiffIndex(previousWord, workingWord)
  }

  // Find optimal path excluding the previously changed position
  let optimalFromHere = pathFinder.findShortestPathExcluding(workingWord, endWord, prevChangedPosition)

  // Fallback if excluding doesn't find a path
  if (!optimalFromHere) {
    optimalFromHere = pathFinder.findShortestPath(workingWord, endWord)
  }

  let hintLetterIdx = -1
  let nextOptimalWord = null

  if (optimalFromHere && optimalFromHere.length > 1) {
    nextOptimalWord = optimalFromHere[1]
    hintLetterIdx = getLetterDiffIndex(workingWord, nextOptimalWord)
  }

  return {
    hintLetterIdx,
    nextOptimalWord,
    wasReverted,
    revertedTo,
    prevChangedPosition
  }
}

/**
 * Simulates the WORD hint (boost) feature behavior.
 * Given a current position, returns what the next word should be.
 *
 * @param {Object} pathFinder - Path finder created with createPathFinder
 * @param {string} startWord - The puzzle's start word
 * @param {string} endWord - The puzzle's end word
 * @param {string} currentWord - The player's current word
 * @param {string[]} currentPath - The player's path (not including start word)
 * @returns {Object} { nextWord, wasReverted, revertedTo }
 */
export function simulateWordHint(pathFinder, startWord, endWord, currentWord, currentPath) {
  // Check for divergence
  const divergence = pathFinder.findDivergencePoint(startWord, endWord, currentPath)

  let workingWord = currentWord
  let workingPath = currentPath
  let wasReverted = false
  let revertedTo = null

  if (divergence.divergeIndex >= 0) {
    // Player has diverged - would revert
    wasReverted = true
    if (divergence.divergeIndex === 0) {
      workingPath = []
      workingWord = startWord
      revertedTo = startWord
    } else {
      workingPath = currentPath.slice(0, divergence.divergeIndex)
      workingWord = workingPath[workingPath.length - 1] || startWord
      revertedTo = workingWord
    }
  }

  // Find optimal path from current position
  const optimalFromHere = pathFinder.findShortestPath(workingWord, endWord)

  let nextWord = null
  if (optimalFromHere && optimalFromHere.length > 1) {
    nextWord = optimalFromHere[1]
  }

  return {
    nextWord,
    wasReverted,
    revertedTo
  }
}
